import { motion } from "motion/react";
import { RotateCcw, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { AdviceCard } from "../components/AdviceCard";
import { ContactModal } from "../components/ContactModal";
import { lowBalanceAdvice, mediumBalanceAdvice, highBalanceAdvice } from "../data/quiz-data";
import Analytics from "../../../utils/analytics";

interface ResultScreenProps {
  onNavigate: (nextScreen: string) => void;
  balanceScore?: number;
}

export function Result({ onNavigate, balanceScore }: ResultScreenProps) {
  //const [searchParams] = useSearchParams();
  const score = balanceScore !== undefined ? balanceScore : 0; // Default score for testing
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const htmlElement = document.querySelector(`[data-component='balance-test']`) as HTMLElement;
  const lowBalanceUrl = htmlElement?.dataset.lowBalanceUrl ?? "";
  const mediumBalanceUrl = htmlElement?.dataset.mediumBalanceUrl ?? "";
  const highBalanceUrl = htmlElement?.dataset.highBalanceUrl ?? "";
  
  // Determine result category
  let resultType: "low" | "medium" | "high";
  let bgColor: string;
  let accentColor: string;
  let title: string;
  let subtitle: string;
  let description: string;
  let advice: typeof lowBalanceAdvice;
  let heroImage: string;
  let balanceUrl: string;

  if (score <= 5) {
    resultType = "low";
    bgColor = "#D8A5A0";
    accentColor = "#D8A5A0";
    title = "Du er i lav balance i dit arbejdsliv";
    subtitle = "Husk dig selv";
    description =
      "Det virker til, at dit arbejde lige nu kræver mere, end du har overskud til. Det er helt okay at have en periode, hvor du har travlt, men husk at prioritere pauser og restitution, så du kan genfinde energien.";
    advice = lowBalanceAdvice;
    heroImage = "/assets/images/balanceTest/balance-asbjorn-250x250px.png";
    balanceUrl = lowBalanceUrl;
  } else if (score <= 9) {
    resultType = "medium";
    bgColor = "#E8B58F";
    accentColor = "#E8B58F";
    title = "Du oplever en smule ubalance i din arbejdsdag";
    subtitle = "Små justeringer kan gøre en forskel";
    description =
      "Du har allerede et godt fundament. Med få ændringer kan du opnå en meget mere stabil arbejdsrytme.";
    advice = mediumBalanceAdvice;
    heroImage = "/assets/images/balanceTest/balance-asbjorn-250x250px.png";
    balanceUrl = mediumBalanceUrl;
  } else {
    resultType = "high";
    bgColor = "#A8C5A0";
    accentColor = "#A8C5A0";
    title = "Du har en god balance i dit arbejdsliv";
    subtitle = "Fortsæt de gode vaner";
    description =
      "Dine nuværende arbejdsvaner fungerer godt. Bliv ved med at prioritere det, der giver dig overskud.";
    advice = highBalanceAdvice;
    heroImage = "/assets/images/balanceTest/balance-asbjorn-250x250px.png";
    balanceUrl = highBalanceUrl;
  }

  function navigateToResult(): void {
    Analytics.getInstance().trackBalanceTestCta("cta_button", resultType, balanceUrl, "Læs vores 5 gode råd");
    window.location.href = balanceUrl;
  }

  return (
    <div className="pt-6 pb-6 background-sand">
      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 pt-12 md:pt-16">
        {/* Title Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="bg-white rounded-3xl p-6 md:p-16 shadow-2xl mb-16"
        >
          <div className="max-w-3xl mx-auto text-center">
            {/* Circular image centered above title */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-8 flex justify-center"
            >
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden">
                <img 
                  src={heroImage} 
                  alt="Balance illustration" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Centered title and subtitle */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mb-8"
            >
              <h1 className="text-4xl md:text-5xl leading-tight mx-[0px] mt-[0px] mb-[8px]" style={{ color: "#E49F72" }}>
                {title}
              </h1>
              <p className="opacity-60 leading-relaxed text-[28px]">
                {subtitle}
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-xl leading-loose opacity-70 mx-[0px] mt-[0px] mb-[24px]"
            >
              {description}
            </motion.p>
            <button
              className="px-10 py-5 rounded-full text-white shadow-xl button hover:shadow-2xl transition-all text-lg hover:scale-105 transform cursor-pointer"
              style={{ backgroundColor: "#D76F2C" }}
              onClick={() => navigateToResult() }
            >
              Læs vores 5 gode råd
            </button>
          </div>
        </motion.div>

        {/* CTA Section - More spacious and calm */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="relative rounded-3xl overflow-hidden mb-20"
          style={{ backgroundColor: "#FBF1EA" }}
        >
          <div className="relative py-16 px-8 md:px-16 text-center" style={{ backgroundColor: "#ffffff" }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1.6, type: "spring", stiffness: 200 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg mb-6"
            >
              <MessageCircle className="w-8 h-8" style={{ color: "#E49F72" }} />
            </motion.div>
            
            <h3 className="text-2xl md:text-3xl mb-4">Har du brug for sparring?</h3>
            <p className="text-lg opacity-70 leading-relaxed max-w-2xl mx-auto mb-8">
              Nogle gange hjælper det at tale med nogen. Vi er her, hvis du har brug for professionel
              støtte til at finde din balance.
            </p>
            <button
              className="px-10 py-5 rounded-full text-white shadow-xl button hover:shadow-2xl transition-all text-lg hover:scale-105 transform cursor-pointer"
              style={{ backgroundColor: "#D76F2C" }}
              onClick={() => {
                Analytics.getInstance().trackBalanceTestCta("contact_button", resultType, "", "");
                setIsContactModalOpen(true);
              }}
            >
              Kontakt os
            </button>
          </div>
        </motion.div> */}

        {/* Retry Button - Moved to bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7 }}
          className="text-center mb-20"
        >
          <button
            onClick={() => {
              onNavigate("welcome");
            }}
            className="px-8 py-4 rounded-full border-2 hover:bg-[var(--warm-sand)] transition-all inline-flex items-center gap-3 group cursor-pointer"
            style={{ borderColor: "#E49F72", color: "#E49F72" }}
          >
            <RotateCcw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
            Tag testen igen
          </button>
        </motion.div>

        {/* Breathing space at bottom */}
        <div className="h-20" />
      </div>

      {/* Contact Modal */}
      {/* <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      /> */}
    </div>
  );
}